import moment from 'moment';
import { FieldLabel, Text } from 'common-components/typography';
import { Avatar } from 'antd';
import { HyperlinkButton } from 'common-components/buttons';
import React from 'react';
import { BookingStatus, BookingType, ServiceType } from 'utilities/enum-utils';
import { BookingTypeIcon } from 'common-components/tags';
import { IRootDispatch } from 'stores/rematch/root-store';
import { useDispatch } from 'react-redux';
import CommonUtils from 'utilities/common-utils';
import PermissionUtils from 'utilities/permission-utils';
import SpinningLoader from 'common-components/loading/SpinningLoader';
import { RecommendedPanel } from 'common-components/roster-control/common/RecommendedPanel';
import { useFetchSessionCapacity } from 'stores/hooks/query-hooks/use-query-fetch-session-capacity';
import { IRosterShift } from '../../types';
import { Badge, Box, Button, Group, Paper, ScrollArea, SimpleGrid } from '@good/ui/core';
import { Link } from 'react-router-dom';
import { usePortalUser } from 'utilities/react-hooks/use-portal-user';
import { AssignWorkerBookingInline } from 'views/bookings-v2/bookings-roster-view/drawers/assign-worker-booking-inline/assign-worker-booking-inline';
import { useBookings } from 'views/bookings-v2/books-view-context/bookings-view-context';
import AssignWorkerGroupInline from '../../drawers/assign-worker-group-inline';

interface OpenSlotDrawerProps {
  shift: IRosterShift;
  refreshAndCloseData: () => void;
  setCanClosePopover: (value: boolean) => void;
}

export const OpenSlotDrawer = ({ shift, refreshAndCloseData, setCanClosePopover }: OpenSlotDrawerProps) => {
  const {
    serviceName,
    shiftStartDateTime,
    shiftEndDateTime,
    duplicateCount,
    customerFirstName,
    customerLastName,
    attachmentUrl,
    serviceId,
    serviceDateTimeId,
  } = shift;
  const onDeselectRosterItem = useBookings((selector) => selector.onDeselectRosterItem);
  const { data: sessionData, isLoading: sessionIsLoading } = useFetchSessionCapacity(
    {
      serviceId,
      serviceDateTimeId,
    },
    shift.serviceType === ServiceType.GROUP,
  );
  const portalUser = usePortalUser();
  const { navigationStore: navigationDispatch, bookingsStore: bookingDispatch } = useDispatch<IRootDispatch>();
  const { setSelectedSideNavMenuKeys } = navigationDispatch;
  const { doAssignWorker } = bookingDispatch;
  const customerFullName = `${customerFirstName} ${customerLastName}`;
  const mStartDate = moment(shiftStartDateTime);
  const mEndDate = moment(shiftEndDateTime);
  const dateDisplay = mStartDate.format('dddd, D MMMM YYYY');
  const timeDisplay = (
    <>
      <b>{mStartDate.format('h:mm a')}</b> - {mEndDate.format('h:mm a')}
    </>
  );

  const goToService = () => {
    setSelectedSideNavMenuKeys(['/services']);
  };

  const goToSession = () => {
    setSelectedSideNavMenuKeys(['/services']);
  };

  const onSubmitAssign = async ({
    selectedWorker,
    editRecurringMode,
    shiftSlotStatus,
    isRemovePendingShiftSlots,
    ignoreShiftClashShiftIds,
  }) => {
    const data = {
      bookingId: shift.bookingId,
      bookingRequestId: shift.bookingRequestId,
      supportWorkerId: selectedWorker.supportWorkerId,
      startDateTime: shift.shiftStartDateTime,
      isRecurring: shift.isRecurring,
      shiftSlotStatus,
      isRemovePendingShiftSlots,
      editRecurringMode,
      bookingType: shift.bookingType,
      doNotSetBookingItem: true,
      ignoreShiftClashShiftIds,
    };
    return await doAssignWorker(data);
  };

  const doRefresh = (doRefreshData = false) => {
    setCanClosePopover(true);
    onDeselectRosterItem();
    doRefreshData && refreshAndCloseData();
  };

  const hasEditWorkerPermissions = PermissionUtils.validatePermission(
    'EditWorkerInBooking',
    portalUser.permissions.permissionRoles,
    shift.serviceDepartmentId,
    shift.serviceId,
  );

  const showActions =
    hasEditWorkerPermissions &&
    ((shift.bookingStatus !== BookingStatus.ARCHIVED && shift.serviceType === ServiceType.INDIVIDUAL) ||
      shift.serviceType === ServiceType.GROUP);

  return (
    <>
      <ScrollArea h='100%'>
        <Box pr='md'>
          <Paper
            p='md'
            bg='yellow.0'
            withBorder
            shadow='lg'
            style={{
              borderColor: 'var(--mantine-color-yellow-4)',
              borderLeft: 'none',
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
            }}
          >
            <div className='flex-column line-height-100 select-none'>
              <Group gap='xs' justify='space-between' wrap='nowrap'>
                <div className='inline-block mb-x-small line-height-100'>
                  {/* TODO : Change this with actual booking status tag */}
                  <div className='flex-row align-center'>
                    <Badge variant='outline' color='orange'>
                      Open slot
                    </Badge>

                    {(duplicateCount ?? 0) > 0 && (
                      <div className='ml-small'>
                        <Text color='orange' size='regular'>
                          <b>{duplicateCount}</b> slots unassigned
                        </Text>
                      </div>
                    )}
                  </div>
                </div>
                <Link
                  to={{
                    pathname:
                      shift.serviceType === ServiceType.GROUP
                        ? `/group-service/${shift.serviceId}/session/details/${shift.serviceDateTimeId}`
                        : `/bookings/details/${shift.bookingId}`,
                    state: {
                      selectedTab: 'TEAM-MEMBERS',
                      from: { url: '/bookings?view=calendar', linkLabel: 'Back to Calendar' },
                    },
                  }}
                >
                  <Button>View</Button>
                </Link>
              </Group>
              <div className='line-height-120 mb-medium'>
                <Text size='x-large' lineHeight={135} weight='bold'>
                  {dateDisplay}
                </Text>
                <br />
                <Text size='x-large' lineHeight={135}>
                  {timeDisplay}
                </Text>
                <br />
                <Text size='x-large' lineHeight={135}>
                  <Text size='x-large' color={'secondary'}>
                    {CommonUtils.formatDurationString(mStartDate, mEndDate)}
                  </Text>
                </Text>
                <br />
                <div className={'flex-row align-center mt-small'}>
                  <BookingTypeIcon
                    type={
                      shift.bookingType === BookingType.ACTIVITY_RECORD
                        ? BookingType.ACTIVITY_RECORD
                        : shift.serviceType
                    }
                    className={'mr-small'}
                  />
                  <Text color={'tertiary'}>{CommonUtils.formatServiceTypeStatus(shift)}</Text>
                </div>
                {shift.serviceType === ServiceType.INDIVIDUAL || !shift.scheduleName ? (
                  <Link
                    to={
                      shift.serviceType === ServiceType.GROUP
                        ? `/group-service/details/${serviceId}`
                        : `/service/details/${serviceId}`
                    }
                  >
                    <HyperlinkButton color={'black'} onClick={goToService}>
                      {serviceName}
                    </HyperlinkButton>
                  </Link>
                ) : (
                  <Text>
                    {shift.scheduleName}{' '}
                    <Link
                      to={
                        shift.serviceType === ServiceType.GROUP
                          ? `/group-service/details/${serviceId}`
                          : `/service/details/${serviceId}`
                      }
                    >
                      <HyperlinkButton color={'secondary'} onClick={goToService}>
                        ({shift.serviceName})
                      </HyperlinkButton>
                    </Link>
                  </Text>
                )}
                {shift.serviceType === ServiceType.GROUP && (
                  <div>
                    <Link to={`/group-service/${serviceId}/session/details/${serviceDateTimeId}`}>
                      <HyperlinkButton onClick={goToSession}>Go to session</HyperlinkButton>
                    </Link>
                  </div>
                )}
              </div>

              {shift.serviceType === ServiceType.GROUP && (
                <div className={'mb-large'}>
                  <div className='mt-x-small'>
                    {sessionIsLoading ? (
                      <SpinningLoader size={20} message={'Fetching recommended capacity...'} />
                    ) : (
                      sessionData?.data && (
                        <RecommendedPanel
                          recommendedTotalShiftSlot={sessionData.data.recommendedTotalShiftSlot}
                          totalAssignedShiftSlot={sessionData.data.totalAssignedShiftSlot}
                        />
                      )
                    )}
                  </div>
                </div>
              )}

              <SimpleGrid cols={2}>
                {shift.bookingId && (
                  <div>
                    <FieldLabel text={'CUSTOMER'} />
                    <div className='flex-row align-center mt-x-small'>
                      <Avatar icon='user' className='mr-small' shape='circle' src={attachmentUrl} />
                      <Text color='secondary'>{customerFullName}</Text>
                    </div>
                  </div>
                )}

                <div>
                  <FieldLabel text={'TEAM MEMBER'} />
                  <div className='flex-row align-center mt-x-small'>
                    <Avatar icon='user' className='mr-small' shape='square' />
                    <Text color='secondary'>Not assigned</Text>
                  </div>
                </div>
              </SimpleGrid>
            </div>
          </Paper>
        </Box>

        {showActions && (
          <Box p='md'>
            {shift.attendanceAddress && shift.serviceType === ServiceType.INDIVIDUAL && (
              <AssignWorkerBookingInline
                key={shift.bookingId}
                onReset={doRefresh}
                serviceId={shift.serviceId}
                bookingId={shift.bookingId}
                bookingRequestId={shift.bookingRequestId}
                onSubmitAssign={onSubmitAssign}
                isRecurring={shift.isRecurring}
                address={shift.attendanceAddress}
                sessions={[]}
                isEditingBooking={true}
                startDateTime={shift.shiftStartDateTime}
                endDateTime={shift.shiftEndDateTime}
                customerUserIds={[shift.bookerUserId!]}
                displayTimezone={shift.timezone}
                bookingType={shift.bookingType}
              />
            )}
            {shift.sessionAddress && shift.serviceType === ServiceType.GROUP && (
              <AssignWorkerGroupInline shiftSlot={shift} onAssigned={doRefresh} session={shift} />
            )}
          </Box>
        )}
      </ScrollArea>
    </>
  );
};
